import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { baseUrl } from '../components/BaseUrl';
import { paystackUrl } from '../components/PaystackUrl';
import { Spinner, Container, Card, Button, Badge, Row, Col } from "react-bootstrap";
import axios from 'axios';
import Swal from "sweetalert2";
import { 
  FaPlay, 
  FaArrowLeft, 
  FaCheckCircle, 
  FaClock, 
  FaStar,
  FaLock,
  FaUnlock,
  FaDatabase
} from 'react-icons/fa';
import '../App.css';

const CourseDescription = () => {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  
  const { id } = useParams();
  const navigate = useNavigate();

  const fetchCourse = useCallback(async () => {
    const token = localStorage.getItem("codefussion-login-token");
    const email = localStorage.getItem('codefussion-student-email')?.trim();
    
    if (!token || !email) {
      navigate('/login');
      return;
    }

    try {
      setLoading(true);
      const userData = { id, email };
      const response = await axios.post(
        `${baseUrl}get_course_by_id.php`, 
        userData,
        { 
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: token
          }
        }
      );

      if (response.data.status === 0) {
        throw new Error('Failed to fetch course');
      }

      setCourse(response.data[0]);
    } catch (error) {
      console.error('Error fetching course:', error);
      Swal.fire({
        title: "Error!",
        text: "Failed to load course details. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
        background: '#1a1a1a',
        color: '#fff',
        confirmButtonColor: '#0066cc'
      });
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const checkEmailVerification = useCallback(async () => {
    const token = localStorage.getItem("codefussion-login-token");
    const email = localStorage.getItem("codefussion-student-email");

    try {
      const response = await axios.post(
        `${baseUrl}check-email-verification.php`, 
        { email },
        { 
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: token
          }
        }
      );

      if (response.data.status === 0) {
        throw new Error('Verification check failed');
      }

      const isVerified = response.data.some(item => item.verify_status === "1");
      return isVerified;
    } catch (error) {
      console.error('Error checking email verification:', error);
      return false;
    }
  }, []);

  const checkEnrollment = useCallback(async (courseId, courseTitle) => {
    const token = localStorage.getItem("codefussion-login-token");
    const email = localStorage.getItem("codefussion-student-email");

    try {
      setEnrolling(true);
      const response = await axios.post(
        `${baseUrl}check-enrollment.php`, 
        { email },
        { 
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: token
          }
        }
      );

      if (response.data.status === 0) {
        Swal.fire({
          title: "Enrollment Required",
          text: "You need to enroll in a track to access this course.",
          icon: "info",
          showDenyButton: true,
          showCancelButton: false,
          confirmButtonText: "Enroll Now",
          denyButtonText: "Browse First",
          background: '#1a1a1a',
          color: '#fff',
          confirmButtonColor: '#0066cc',
          denyButtonColor: '#6c757d'
        }).then((result) => {
          if (result.isConfirmed) {
            navigate('/tracks');
          } else {
            navigateToCourseOutline(courseId, courseTitle);
          }
        });
      } else {
        navigateToCourseOutline(courseId, courseTitle);
      }
    } catch (error) {
      console.error('Error checking enrollment:', error);
      Swal.fire({
        title: "Error!",
        text: "Failed to check enrollment status.",
        icon: "error",
        confirmButtonText: "OK",
        background: '#1a1a1a',
        color: '#fff',
        confirmButtonColor: '#0066cc'
      });
    } finally {
      setEnrolling(false);
    }
  }, [navigate]);

  const navigateToCourseOutline = (courseId, courseTitle) => {
    const courseRoutes = {
      'html 5': '/html-course-outline',
      'javascript': '/javascript-course-outline',
      'css': '/css-course-outline',
      'react js': '/react-course-outline',
      'php': '/php-course-outline',
      'color psychology': '/color-psychology-course-outline',
      'javascript for cybersecurity': '/cyber-script-course-outline',
      'mysql': '/mysql-course-outline'
    };

    const route = courseRoutes[courseTitle.toLowerCase().trim()];
    if (route) {
      navigate(route);
    } else {
      navigate(`/read-course/${courseId}`);
    }
  };

  const handleStartLearning = async () => {
    if (!course) return;

    const isEmailVerified = await checkEmailVerification();
    
    if (!isEmailVerified) {
      Swal.fire({
        title: "Email Verification Required",
        text: "Please verify your email to continue with the course.",
        icon: "warning",
        confirmButtonText: "OK",
        background: '#1a1a1a',
        color: '#fff',
        confirmButtonColor: '#0066cc'
      });
      return;
    }

    await checkEnrollment(course.id, course.course_title);
  };

  useEffect(() => {
    fetchCourse();
  }, [fetchCourse]);

  if (!localStorage.getItem("codefussion-login-token")) {
    navigate('/login');
    return null;
  }

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted">Loading course details...</p>
        </div>
      </Container>
    );
  }

  if (!course) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <Card className="text-center p-4 shadow">
          <Card.Body>
            <h4>Course Not Found</h4>
            <p className="text-muted">The requested course could not be loaded.</p>
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const canAccessCourse = localStorage.getItem("codefussion-subscription-status") === "active" || 
                         course.subscription_required === "false";

  const objectives = [
    course.first_course_objective,
    course.second_course_objective,
    course.third_course_objective,
    course.fourth_course_objective,
    course.fifth_course_objective
  ].filter(obj => obj && obj.trim() !== '');

  return (
    <div>
      {/* Header with Back Button - FIXED POSITION */}
      <div className="container p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button 
          variant="outline-primary" 
          onClick={() => navigate('/dashboard')}
          className="d-flex align-items-center gap-2"
        >
          <FaArrowLeft /> Back to Dashboard
        </Button>
        
        <div>
          {/* <Badge bg="light" text="dark" className="fs-6">
            <FaStar className="text-warning me-1" />
            Featured
          </Badge> */}
          <Badge bg={canAccessCourse ? "success" : "warning"} className="fs-6">
            {canAccessCourse ? <FaUnlock className="me-1" /> : <FaLock className="me-1" />}
            {canAccessCourse ? "Access Granted" : "Subscription Required"}
          </Badge>
        </div>
      </div>
      </div>
   
    <Container className="py-4">
      {/* Main Course Card */}
      <Card className="shadow-lg border-0 modern-card">
        <Card.Body className="p-4">
          <Row>
            {/* Left Column - Course Content */}
            <Col lg={8}>
              {/* Course Header */}
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="bg-primary rounded-circle p-3">
                  <FaDatabase className="text-white" size={24} />
                </div>
                <div>
                  <h1 className="h2 fw-bold text-dark mb-1">{course.course_title}</h1>
                </div>
              </div>
              
              <p className="lead mb-4">{course.course_description}</p>

              {/* Course Stats */}
              <div className="d-flex gap-4 mb-4">
                <div className="d-flex align-items-center gap-2 text-muted">
                  <FaClock className="text-primary" />
                  <small>Self-paced</small>
                </div>
                <div className="d-flex align-items-center gap-2 text-muted">
                  <FaCheckCircle className="text-success" />
                  <small>{objectives.length} Learning Objectives</small>
                </div>
              </div>

              {/* Prerequisites */}
              <div className="mb-5">
                <h4 className="fw-bold mb-3 text-dark">
                  <FaLock className="me-2 text-warning" />
                  Prerequisites
                </h4>
                <Card className="bg-light border-0">
                  <Card.Body className="p-3">
                    <p className="mb-0 text-muted">
                      {course.course_prerequiste && course.course_prerequiste.trim() !== '' 
                        ? course.course_prerequiste 
                        : 'No prerequisites required'
                      }
                    </p>
                  </Card.Body>
                </Card>
              </div>

              {/* Learning Objectives */}
              <div className="mb-4">
                <h4 className="fw-bold mb-3 text-dark">
                  <FaCheckCircle className="me-2 text-success" />
                  What You'll Learn
                </h4>
                <div className="row">
                  {objectives.map((objective, index) => (
                    <div key={index} className="col-12 mb-3">
                      <div className="d-flex align-items-start gap-3 p-3 bg-light rounded">
                        <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                        <span className="text-dark">{objective}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Col>

            {/* Right Column - Action Panel */}
            <Col lg={4}>
              <Card className="border-0 shadow-sm sticky-top" style={{ top: '100px' }}>
                <Card.Body className="text-center p-4">
                  {canAccessCourse ? (
                    <Button
                      variant="primary"
                      size="lg"
                      className="w-100 mb-3 d-flex align-items-center text-light border-light justify-content-center gap-2 py-3"
                      onClick={handleStartLearning}
                      disabled={enrolling}
                    >
                      {enrolling ? (
                        <>
                          <Spinner animation="border" size="sm" />
                          Checking Access...
                        </>
                      ) : (
                        <>
                          <FaPlay />
                          Start Learning
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="warning"
                      size="lg"
                      className="w-100 mb-3 py-3"
                      onClick={() => navigate('/plans')}
                    >
                      <FaLock className="me-2" />
                      Subscribe to Access
                    </Button>
                  )}
                  
                  <div className="text-center">
                    <small className="text-dark">
                      {canAccessCourse 
                        ? "Full access to all course materials" 
                        : "Subscribe to unlock this course and all features"
                      }
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
     </div>
  );
};

export default CourseDescription;